/* ==========================================================================
   Podsmania — motion.js
   Scroll reveals, multi-layer parallax, sticky scroll-scrub, cursor parallax,
   count-up and blur-up images.

   Design notes:
   - ONE requestAnimationFrame loop drives every effect. No per-element scroll
     listeners, and the loop parks itself when nothing is on screen.
   - Each frame reads all geometry first, then writes all transforms, so we
     never interleave reads and writes (layout thrash).
   - Only `transform` and `opacity` are animated — both composited.
   - IntersectionObserver decides what participates; off-screen elements are
     skipped entirely.
   - Kills itself on `prefers-reduced-motion` or the `.motion-off` class that
     the theme setting applies.
   ========================================================================== */

(function () {
  "use strict";

  var root = document.documentElement;
  var reducedQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

  var Motion = {
    parallax: [],
    scrubs: [],
    cursorTargets: [],
    ticking: false,
    active: false,
    pointer: { x: 0, y: 0, tx: 0, ty: 0, active: false },
    intensity: 1
  };

  function motionAllowed() {
    return !reducedQuery.matches && !root.classList.contains("motion-off");
  }

  function clamp(value, min, max) {
    return value < min ? min : value > max ? max : value;
  }

  /* ------------------------------------------------------------------------
     Scroll reveal
     ------------------------------------------------------------------------ */

  var revealObserver = null;

  function initReveals(scope) {
    var targets = (scope || document).querySelectorAll("[data-reveal]:not(.is-revealed)");
    if (!targets.length) return;

    if (!motionAllowed() || !("IntersectionObserver" in window)) {
      for (var i = 0; i < targets.length; i++) targets[i].classList.add("is-revealed");
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            entry.target.classList.add("is-revealed");
            // One-shot: never re-hide content the visitor has already seen.
            revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: "0px 0px -8% 0px", threshold: 0.08 }
      );
    }

    targets.forEach(function (el) {
      // Stagger children of a group so a grid cascades instead of popping.
      var group = el.closest("[data-reveal-group]");
      if (group && !el.style.getPropertyValue("--stagger-index")) {
        var siblings = group.querySelectorAll("[data-reveal]");
        var index = Array.prototype.indexOf.call(siblings, el);
        if (index > -1) {
          el.style.setProperty("--stagger-index", String(Math.min(index, 12)));
        }
      }
      revealObserver.observe(el);
    });
  }

  /* ------------------------------------------------------------------------
     Parallax registration
     ------------------------------------------------------------------------ */

  var visibilityObserver = null;

  function ensureVisibilityObserver() {
    if (visibilityObserver || !("IntersectionObserver" in window)) return;
    visibilityObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var record = entry.target._motionRecord;
          if (record) record.visible = entry.isIntersecting;
        });
        requestTick();
      },
      { rootMargin: "20% 0px 20% 0px" }
    );
  }

  function initParallax(scope) {
    if (!motionAllowed()) return;
    ensureVisibilityObserver();

    var nodes = (scope || document).querySelectorAll("[data-parallax]");
    nodes.forEach(function (el) {
      if (el._motionRecord) return;

      var record = {
        el: el,
        speed: parseFloat(el.getAttribute("data-parallax-speed") || "0.18"),
        axis: el.getAttribute("data-parallax-axis") || "y",
        max: parseFloat(el.getAttribute("data-parallax-max") || "160"),
        visible: false,
        offset: 0,
        rendered: null
      };

      el._motionRecord = record;
      Motion.parallax.push(record);
      if (visibilityObserver) visibilityObserver.observe(el);
      else record.visible = true;
    });

    requestTick();
  }

  function initScrubs(scope) {
    var nodes = (scope || document).querySelectorAll("[data-scrub]");
    nodes.forEach(function (el) {
      if (el._scrubRecord) return;

      var record = {
        el: el,
        steps: parseInt(el.getAttribute("data-scrub-steps") || "0", 10),
        visible: false,
        progress: -1,
        step: -1
      };

      el._scrubRecord = record;
      el._motionRecord = record; // shares the visibility observer
      Motion.scrubs.push(record);

      ensureVisibilityObserver();
      if (visibilityObserver) visibilityObserver.observe(el);
      else record.visible = true;

      applyScrubMode(record);
    });

    requestTick();
  }

  /**
   * Without motion there is no scroll to drive the effect, so the section
   * drops its tall track and lays every step out statically instead. The
   * class is the single switch; the CSS does the rest.
   */
  function applyScrubMode(record) {
    var isStatic = !motionAllowed();
    record.el.classList.toggle("scroll-showcase--static", isStatic);

    if (isStatic) {
      record.el.style.removeProperty("--scrub-progress");
      record.el.removeAttribute("data-scrub-step");
      record.progress = -1;
      record.step = -1;
    } else if (!record.el.hasAttribute("data-scrub-step")) {
      record.el.setAttribute("data-scrub-step", "0");
    }
  }

  /* ------------------------------------------------------------------------
     Cursor parallax (desktop only)
     ------------------------------------------------------------------------ */

  function initCursorParallax(scope) {
    if (!motionAllowed()) return;
    if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;

    var nodes = (scope || document).querySelectorAll("[data-cursor-parallax]");
    if (!nodes.length) return;

    nodes.forEach(function (el) {
      if (el._cursorRecord) return;
      var record = {
        el: el,
        strength: parseFloat(el.getAttribute("data-cursor-parallax") || "12"),
        x: 0,
        y: 0
      };
      el._cursorRecord = record;
      Motion.cursorTargets.push(record);
    });

    if (!Motion.pointer.active) {
      Motion.pointer.active = true;
      window.addEventListener(
        "pointermove",
        function (event) {
          // Normalised to -1..1 from the viewport centre.
          Motion.pointer.tx = (event.clientX / window.innerWidth) * 2 - 1;
          Motion.pointer.ty = (event.clientY / window.innerHeight) * 2 - 1;
          requestTick();
        },
        { passive: true }
      );
    }
  }

  /* ------------------------------------------------------------------------
     The single rAF loop
     ------------------------------------------------------------------------ */

  function requestTick() {
    if (Motion.ticking) return;
    Motion.ticking = true;
    window.requestAnimationFrame(frame);
  }

  function frame() {
    Motion.ticking = false;

    if (!motionAllowed()) {
      resetAll();
      return;
    }

    var viewportH = window.innerHeight;
    var viewportCentre = viewportH / 2;
    var intensity = Motion.intensity;
    var writes = [];
    var needsAnotherFrame = false;

    /* --- READ PHASE: gather geometry, mutate nothing ---------------------- */

    for (var i = 0; i < Motion.parallax.length; i++) {
      var p = Motion.parallax[i];
      if (!p.visible) continue;

      var rect = p.el.getBoundingClientRect();
      var centre = rect.top + rect.height / 2;
      var distance = viewportCentre - centre;
      var offset = clamp(distance * p.speed * intensity, -p.max, p.max);

      // Sub-pixel changes aren't worth a style write.
      if (p.rendered === null || Math.abs(offset - p.rendered) > 0.15) {
        p.rendered = offset;
        writes.push({ record: p, offset: offset, kind: "parallax" });
      }
    }

    for (var s = 0; s < Motion.scrubs.length; s++) {
      var scrub = Motion.scrubs[s];
      if (!scrub.visible) continue;

      var sRect = scrub.el.getBoundingClientRect();
      var scrollable = sRect.height - viewportH;
      var progress = scrollable > 0 ? clamp(-sRect.top / scrollable, 0, 1) : sRect.top < 0 ? 1 : 0;

      if (scrub.progress === -1 || Math.abs(progress - scrub.progress) > 0.001) {
        scrub.progress = progress;
        writes.push({ record: scrub, offset: progress, kind: "scrub" });
      }
    }

    /* --- Cursor easing (pure maths, no DOM) ------------------------------- */

    if (Motion.cursorTargets.length) {
      var ease = 0.08;
      Motion.pointer.x += (Motion.pointer.tx - Motion.pointer.x) * ease;
      Motion.pointer.y += (Motion.pointer.ty - Motion.pointer.y) * ease;

      var settled =
        Math.abs(Motion.pointer.tx - Motion.pointer.x) < 0.001 &&
        Math.abs(Motion.pointer.ty - Motion.pointer.y) < 0.001;

      if (!settled) needsAnotherFrame = true;

      for (var c = 0; c < Motion.cursorTargets.length; c++) {
        var t = Motion.cursorTargets[c];
        writes.push({
          record: t,
          kind: "cursor",
          x: Motion.pointer.x * t.strength * intensity,
          y: Motion.pointer.y * t.strength * intensity
        });
      }
    }

    /* --- WRITE PHASE: apply every style change together ------------------- */

    for (var w = 0; w < writes.length; w++) {
      var job = writes[w];
      if (job.kind === "parallax") {
        var axis = job.record.axis;
        job.record.el.style.setProperty(
          axis === "x" ? "--parallax-x" : "--parallax-y",
          job.offset.toFixed(2) + "px"
        );
        if (axis === "x") {
          job.record.el.style.transform =
            "translate3d(" + job.offset.toFixed(2) + "px, 0, 0)";
        }
      } else if (job.kind === "scrub") {
        job.record.el.style.setProperty("--scrub-progress", job.offset.toFixed(4));
        if (job.record.steps > 0) {
          var step = Math.min(
            job.record.steps - 1,
            Math.floor(job.offset * job.record.steps)
          );
          if (step !== job.record.step) {
            job.record.step = step;
            job.record.el.setAttribute("data-scrub-step", String(step));
          }
        }
      } else if (job.kind === "cursor") {
        job.record.el.style.setProperty("--cursor-x", job.x.toFixed(2) + "px");
        job.record.el.style.setProperty("--cursor-y", job.y.toFixed(2) + "px");
      }
    }

    if (needsAnotherFrame) requestTick();
  }

  function resetAll() {
    Motion.parallax.forEach(function (p) {
      p.el.style.removeProperty("--parallax-y");
      p.el.style.removeProperty("--parallax-x");
      p.el.style.transform = "";
      p.rendered = null;
    });
    Motion.cursorTargets.forEach(function (t) {
      t.el.style.removeProperty("--cursor-x");
      t.el.style.removeProperty("--cursor-y");
    });
  }

  /* ------------------------------------------------------------------------
     Count-up numbers
     ------------------------------------------------------------------------ */

  function initCounters(scope) {
    var nodes = (scope || document).querySelectorAll("[data-count-to]:not([data-counted])");
    if (!nodes.length) return;

    if (!motionAllowed() || !("IntersectionObserver" in window)) {
      nodes.forEach(function (el) {
        el.textContent = formatCount(parseFloat(el.getAttribute("data-count-to")), el);
        el.setAttribute("data-counted", "true");
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          runCount(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.4 }
    );

    nodes.forEach(function (el) {
      observer.observe(el);
    });
  }

  function formatCount(value, el) {
    var decimals = parseInt(el.getAttribute("data-count-decimals") || "0", 10);
    var prefix = el.getAttribute("data-count-prefix") || "";
    var suffix = el.getAttribute("data-count-suffix") || "";
    return prefix + value.toFixed(decimals).replace(".", ",") + suffix;
  }

  function runCount(el) {
    var target = parseFloat(el.getAttribute("data-count-to"));
    if (isNaN(target)) return;

    el.setAttribute("data-counted", "true");
    var duration = parseInt(el.getAttribute("data-count-duration") || "1400", 10);
    var start = null;

    function step(timestamp) {
      if (start === null) start = timestamp;
      var progress = clamp((timestamp - start) / duration, 0, 1);
      // easeOutExpo — fast start, gentle settle.
      var eased = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      el.textContent = formatCount(target * eased, el);
      if (progress < 1) window.requestAnimationFrame(step);
    }

    window.requestAnimationFrame(step);
  }

  /* ------------------------------------------------------------------------
     Blur-up images
     ------------------------------------------------------------------------ */

  function initBlurUp(scope) {
    var images = (scope || document).querySelectorAll("img[data-blur-up]:not(.is-loaded)");
    images.forEach(function (img) {
      if (img.complete && img.naturalWidth > 0) {
        img.classList.add("is-loaded");
        return;
      }
      img.addEventListener(
        "load",
        function () {
          img.classList.add("is-loaded");
        },
        { once: true }
      );
      // A broken image must not stay blurred forever.
      img.addEventListener(
        "error",
        function () {
          img.classList.add("is-loaded");
        },
        { once: true }
      );
    });
  }


  /* ------------------------------------------------------------------------
     Hero video

     Autoplaying background video is cheap to start and expensive to leave
     running: it burns battery and decode time even when scrolled past. So it
     pauses off-screen, pauses under `prefers-reduced-motion`, and exposes a
     manual control (WCAG 2.2.2 — looping media needs a way to stop it). A
     manual pause always wins over the automatic resume.
     ------------------------------------------------------------------------ */

  function initHeroVideo(scope) {
    var wraps = (scope || document).querySelectorAll("[data-hero-video-wrap]:not([data-video-ready])");

    Array.prototype.forEach.call(wraps, function (wrap) {
      var video = wrap.querySelector("video");
      if (!video) return;

      wrap.setAttribute("data-video-ready", "true");
      var pausedByUser = false;

      function play() {
        if (pausedByUser || !motionAllowed()) return;
        var attempt = video.play();
        // Autoplay can still be refused (low power mode, data saver); that is
        // not an error worth surfacing — the poster stays up.
        if (attempt && typeof attempt.catch === "function") attempt.catch(function () {});
      }

      if (!motionAllowed()) video.pause();

      var toggle = wrap.querySelector("[data-hero-video-toggle]");
      if (toggle) {
        var label = toggle.querySelector("[data-hero-video-label]");
        var iconPause = toggle.querySelector("[data-icon-pause]");
        var iconPlay = toggle.querySelector("[data-icon-play]");

        // Drive the button from the video's real state, not from the click.
        // Autoplay gets refused often enough on real phones (low power mode,
        // data saver) that a button assuming "it is playing" would sit there
        // offering to pause something that never started.
        function syncToggle() {
          var isPaused = video.paused;
          toggle.setAttribute("aria-pressed", isPaused ? "true" : "false");
          if (iconPause) iconPause.hidden = isPaused;
          if (iconPlay) iconPlay.hidden = !isPaused;
          if (label) {
            label.textContent = isPaused
              ? label.getAttribute("data-play-label") || "Redă videoclipul"
              : label.getAttribute("data-pause-label") || "Oprește videoclipul";
          }
        }

        video.addEventListener("play", syncToggle);
        video.addEventListener("pause", syncToggle);
        syncToggle();

        toggle.addEventListener("click", function () {
          if (video.paused) {
            // An explicit tap overrides both the earlier pause and reduced
            // motion — the visitor asked for it.
            pausedByUser = false;
            var attempt = video.play();
            if (attempt && typeof attempt.catch === "function") attempt.catch(function () {});
          } else {
            pausedByUser = true;
            video.pause();
          }
        });
      }

      if ("IntersectionObserver" in window) {
        var observer = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) play();
              else video.pause();
            });
          },
          { threshold: 0.05 }
        );
        observer.observe(wrap);
      }
    });
  }

  /* ------------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------------ */

  function init(scope) {
    var intensityAttr = root.getAttribute("data-motion-intensity");
    if (intensityAttr) Motion.intensity = parseFloat(intensityAttr) / 100;

    initReveals(scope);
    initParallax(scope);
    initScrubs(scope);
    initCursorParallax(scope);
    initCounters(scope);
    initBlurUp(scope);
    initHeroVideo(scope);

    if (!Motion.active) {
      Motion.active = true;
      window.addEventListener("scroll", requestTick, { passive: true });
      window.addEventListener("resize", onResize, { passive: true });
      window.addEventListener("orientationchange", onResize, { passive: true });
    }

    requestTick();
  }

  var resizeTimer = null;

  function onResize() {
    // Invalidate cached offsets so the next frame re-measures from scratch.
    Motion.parallax.forEach(function (p) {
      p.rendered = null;
    });
    Motion.scrubs.forEach(function (s) {
      s.progress = -1;
    });
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(requestTick, 60);
  }

  function purgeDetached() {
    Motion.parallax = Motion.parallax.filter(function (p) {
      return p.el.isConnected;
    });
    Motion.scrubs = Motion.scrubs.filter(function (s) {
      return s.el.isConnected;
    });
    Motion.cursorTargets = Motion.cursorTargets.filter(function (t) {
      return t.el.isConnected;
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      init(document);
    });
  } else {
    init(document);
  }

  // Re-scan when a merchant edits a section in the theme editor.
  document.addEventListener("shopify:section:load", function (event) {
    purgeDetached();
    init(event.target);
  });

  document.addEventListener("shopify:section:unload", purgeDetached);

  // React live if the visitor flips the OS reduced-motion switch.
  var onPreferenceChange = function () {
    if (!motionAllowed()) resetAll();
    else requestTick();
    Motion.scrubs.forEach(applyScrubMode);
    initReveals(document);
  };

  if (typeof reducedQuery.addEventListener === "function") {
    reducedQuery.addEventListener("change", onPreferenceChange);
  } else if (typeof reducedQuery.addListener === "function") {
    reducedQuery.addListener(onPreferenceChange);
  }

  // Exposed so other theme scripts can re-scan markup they inject.
  window.PodsmaniaMotion = {
    refresh: function (scope) {
      purgeDetached();
      init(scope || document);
    }
  };
})();
